import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { MapPin, Phone, Loader2 } from "lucide-react";

type PartnerMiniData = {
  leadId: string | null;
  partnerId: string;
  name: string;
  title: string;
  location: string;
  phone: string;
  mapsUrl: string | null;
  cover: {
    type: string;
    label: string;
    url?: string;
  };
  description: string;
};

const PartnerMiniPage: React.FC = () => {
  const { leadId, partnerId } = useParams<{ leadId?: string; partnerId?: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<PartnerMiniData | null>(null);

  const telHref = useMemo(() => {
    if (!data?.phone) return "";
    const cleanPhone = data.phone.replace(/\s+/g, "");
    return `tel:${cleanPhone}`;
  }, [data?.phone]);

  const trackEvent = async (event: "view" | "call_click" | "maps_click", payload?: PartnerMiniData) => {
    const current = payload || data;
    if (!current) return;

    try {
      if (!current.leadId) return;

      await fetch("/api/track-partner-mini.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          leadId: current.leadId,
          partnerId: current.partnerId,
          event,
        }),
        keepalive: true,
      });
    } catch (err) {
      console.error("Tracking error:", err);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      if (!leadId && !partnerId) {
        setError("Μη έγκυρο link");
        setLoading(false);
        return;
      }

      try {
        const query = leadId
          ? `leadId=${encodeURIComponent(leadId)}`
          : `partnerId=${encodeURIComponent(partnerId as string)}`;

        const response = await fetch(`/api/get-partner-mini.php?${query}`);
        const result = await response.json();

        if (!response.ok) {
          throw new Error(result?.error || "Δεν βρέθηκαν στοιχεία");
        }

        setData(result);
        if (result?.leadId) {
          await trackEvent("view", result);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Σφάλμα φόρτωσης");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [leadId, partnerId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="flex flex-col items-center gap-3 text-slate-600">
          <Loader2 className="h-8 w-8 animate-spin" />
          <p>Φόρτωση στοιχείων συνεργάτη...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center space-y-3">
            <h1 className="text-lg font-semibold text-slate-900">Δεν βρέθηκαν στοιχεία συνεργάτη</h1>
            <p className="text-sm text-slate-600">{error || "Δοκιμάστε ξανά αργότερα."}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-6 px-4">
      <div className="max-w-md mx-auto space-y-4">
        <div
          className={`rounded-2xl text-white p-6 shadow-lg ${
            data.cover?.type === "image" && data.cover?.url
              ? "bg-slate-900"
              : "bg-gradient-to-r from-emerald-500 to-green-600"
          }`}
          style={
            data.cover?.type === "image" && data.cover?.url
              ? {
                  backgroundImage: `linear-gradient(to right, rgba(15, 23, 42, 0.75), rgba(15, 23, 42, 0.45)), url(${data.cover.url})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }
              : undefined
          }
        >
          <div className="h-12 w-12 rounded-full bg-white/20 flex items-center justify-center text-lg font-bold mb-3">
            {data.cover?.label || data.name.charAt(0)}
          </div>
          <h1 className="text-xl font-bold">{data.name}</h1>
          <p className="text-white/90 text-sm mt-1">{data.title}</p>
        </div>

        <Card>
          <CardContent className="p-5 space-y-4">
            <p className="text-sm text-slate-700 leading-relaxed">{data.description}</p>

            <div className="rounded-lg border border-slate-200 p-3 bg-white">
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 mt-0.5 text-slate-500" />
                <p className="text-sm text-slate-700">{data.location || "Δεν έχει δηλωθεί τοποθεσία"}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-2">
              <Button asChild className="w-full bg-emerald-600 hover:bg-emerald-700">
                <a
                  href={telHref}
                  onClick={() => trackEvent("call_click")}
                >
                  <Phone className="mr-2 h-4 w-4" />
                  Κλήση συνεργάτη
                </a>
              </Button>

              {data.mapsUrl && (
                <Button variant="outline" asChild className="w-full">
                  <a
                    href={data.mapsUrl}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => trackEvent("maps_click")}
                  >
                    <MapPin className="mr-2 h-4 w-4" />
                    Άνοιγμα στο Google Maps
                  </a>
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PartnerMiniPage;
